import * as Immutable from 'immutable';
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../Common/_hooks/useAppSelector";
import { Button } from "@mui/material";
import { useAppDispatch } from "../../../Common/_hooks/useAppDispatch";
import { useCallback, useMemo, useState } from "react";
import { ClearStoreSelectedProduct, SelectAllStoreSelectedProduct } from "../../../UIData/_actions/Account/AccountStoreActions";
import { useParams } from "react-router";
import BatchIgnoreStoreProductsDialog from "./BatchIgnoreStoreProductsDialog";
import BatchUnsyncStoreProductsDialog from "./BatchUnsyncStoreProductsDialog";
import BatchDeleteStoreProductsDialog from "./BatchDeleteStoreProductsDialog";
import { BatchUnignoreStoreProducts, type BatchStoreProductSelection } from "../../../UserData/_actions/Store/StoreProductActions";
import { type StoreProductSyncState } from "../../../UserData/_stores/Store/StoreProductStore";
import useCheckStoreFeatures from "../../../Common/_hooks/Store/useCheckStoreFeatures";
import BatchSelectionActionBar from "../../BatchSelectionActionBar";
import DeleteIcon from '../../../Common/Icons/DeleteIcon';

interface Props {
  listId: string
  search: {
    search: string[]
    productTypes: string[]
    state: StoreProductSyncState[]
  }
}

const buildBatchSelection = (
  storeSelectedProducts: Immutable.OrderedSet<number>,
  storeSelectedAllProducts: boolean,
  storeExcludedSelectedProducts: Immutable.OrderedSet<number>,
  search: Props['search'],
): BatchStoreProductSelection => ({
  ids: storeSelectedProducts.toArray(),
  selectAll: storeSelectedAllProducts,
  excludeIds: storeExcludedSelectedProducts.toArray(),
  filters: {
    search: search.search,
    state: search.state,
    productTypes: search.productTypes,
  },
})

export default function StoreProductListActions(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const params = useParams<{ storeSlug: string }>()

  const store = useAppSelector(state => state.get('userData').get('stores').find(store => store.get('slug') === params.storeSlug))
  const storeSelectedProducts = useAppSelector(state => state.get('UIData').get('account').get('storeSelectedProducts'))
  const storeSelectedAllProducts = useAppSelector(state => state.get('UIData').get('account').get('storeSelectedAllProducts'))
  const storeExcludedSelectedProducts = useAppSelector(state => state.get('UIData').get('account').get('storeExcludedSelectedProducts'))
  const totalMatchingProducts = useAppSelector(state =>
    state.get('userData')
      .get('stores')
      .find(store => store.get('slug') === params.storeSlug)
      ?.get('products')
      ?.get('itemLists')
      ?.get(props.listId)
      ?.get('total') ?? 0)
  const [ignoreDialogOpened, setIgnoreDialogOpened] = useState(false)
  const [unsyncDialogOpened, setUnsyncDialogOpened] = useState(false)
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false)
  const { canDeleteRemoteProduct } = useCheckStoreFeatures(params.storeSlug ?? '')

  const selectedCount = useMemo(() => {
    if (storeSelectedAllProducts) {
      return Math.max(totalMatchingProducts - storeExcludedSelectedProducts.count(), 0)
    }

    return storeSelectedProducts.count()
  }, [storeExcludedSelectedProducts, storeSelectedAllProducts, storeSelectedProducts, totalMatchingProducts])

  const remainingToSelect = useMemo(() => {
    return Math.max(totalMatchingProducts - selectedCount, 0)
  }, [selectedCount, totalMatchingProducts])

  const getBatchSelection = useCallback((): BatchStoreProductSelection => (
    buildBatchSelection(
      storeSelectedProducts,
      storeSelectedAllProducts,
      storeExcludedSelectedProducts,
      props.search,
    )
  ), [props.search, storeExcludedSelectedProducts, storeSelectedAllProducts, storeSelectedProducts])

  const hasDialogOpen = ignoreDialogOpened || unsyncDialogOpened || (canDeleteRemoteProduct && deleteDialogOpened)

  const dialogSelection = useMemo(() => {
    if (!hasDialogOpen) {
      return null
    }

    return getBatchSelection()
  }, [getBatchSelection, hasDialogOpen])

  const areAllSelectedProductsIgnored = useMemo(() => {
    if (!store || selectedCount === 0) {
      return false
    }

    if (storeSelectedAllProducts) {
      return props.search.state.length === 1 && props.search.state[0] === 'ignored'
    }

    return storeSelectedProducts.every((storeProductId) => {
      const storeProduct = store.get('products').get('data').get(String(storeProductId))

      if (!storeProduct) {
        return false
      }

      return storeProduct.get('sync_state') === 'ignored'
    })
  }, [props.search.state, selectedCount, store, storeSelectedAllProducts, storeSelectedProducts])

  const onClear = useCallback(() => {
    dispatch(ClearStoreSelectedProduct())
  }, [dispatch])

  const onSelectAll = useCallback(() => {
    dispatch(SelectAllStoreSelectedProduct())
  }, [dispatch])

  const onIgnore = useCallback(() => {
    setIgnoreDialogOpened(true)
  }, [])

  const onUnsync = useCallback(() => {
    setUnsyncDialogOpened(true)
  }, [])

  const onDelete = useCallback(() => {
    setDeleteDialogOpened(true)
  }, [])

  const onUnignore = useCallback(() => {
    if (!store) return

    dispatch(BatchUnignoreStoreProducts(store.get('slug'), getBatchSelection()).set({
      onSuccess: () => {
        dispatch(ClearStoreSelectedProduct())
      }
    }))
  }, [dispatch, getBatchSelection, store])

  if (!store) {
    return null
  }

  return <>
    <BatchSelectionActionBar
      selectedCount={selectedCount}
      totalMatchingCount={totalMatchingProducts}
      selectAllActive={storeSelectedAllProducts}
      onClear={onClear}
      onSelectAll={remainingToSelect > 0 ? onSelectAll : undefined}
      actions={<>
        {areAllSelectedProductsIgnored
          ? <Button
            color="warning"
            onClick={onUnignore}
          >{t('Unignore')}</Button>
          : <Button
            color="warning"
            onClick={onIgnore}
          >{t('Ignore')}</Button>}
        <Button
          color="secondary"
          onClick={onUnsync}
        >{t('Unsync')}</Button>
        {canDeleteRemoteProduct ? <Button
          color="error"
          onClick={onDelete}
          startIcon={<DeleteIcon />}
        >{t('Delete')}</Button> : null}
      </>}
    />

    {ignoreDialogOpened && dialogSelection ? <BatchIgnoreStoreProductsDialog
      opened={ignoreDialogOpened}
      storeName={store.get('name')}
      storeType={store.get('slug')}
      selection={dialogSelection}
      selectionCount={selectedCount}
      onClose={() => setIgnoreDialogOpened(false)}
    /> : null}

    {unsyncDialogOpened && dialogSelection ? <BatchUnsyncStoreProductsDialog
      opened={unsyncDialogOpened}
      storeType={store.get('slug')}
      selection={dialogSelection}
      selectionCount={selectedCount}
      onClose={() => setUnsyncDialogOpened(false)}
    /> : null}

    {deleteDialogOpened && dialogSelection && canDeleteRemoteProduct ? <BatchDeleteStoreProductsDialog
      opened={deleteDialogOpened}
      storeName={store.get('name')}
      storeType={store.get('slug')}
      selection={dialogSelection}
      selectionCount={selectedCount}
      onClose={() => setDeleteDialogOpened(false)}
    /> : null}
  </>
}
